import React from 'react';

import { useFullVariantByIdSsr } from '../../../api/hooks/useFullVariantByIdSsr.hook';
import { useInstanceDetailPage } from '../../../api/hooks/useInstanceDetail.hook';
import { VariantImageType } from '../../../api/models/variant-image-type';
import { RootState, useAppSelector } from '../../../redux';
import { TypographyVariantsEnum } from '../../../utils/_enums/typeography-variants.enum';
import { EnumToLanguageMapper } from '../../../utils/_helper/enum-to-language-mapper';
import useBreakpoint from '../../../utils/_hooks/useBreakpoints.hook';
import useFilterAttachments from '../../../utils/_hooks/useFilterAttachments.hook';
import Accordion from '../../_shared/accordion/accordion';
import { Typography } from '../../_shared/typography/typography';
import { InstanceDetailUnlockableFile } from './instance-detail-unlockable-file/instance-detail-unlockable-file';
import { InstanceDetailUnlockableImage } from './instance-detail-unlockable-image/instance-detail-unlockable-image';
import styles from './instance-detail-unlockables.module.scss';

interface InstanceDetailUnlockablesProps {
    instanceCoverArtUrl: string;
}

export const InstanceDetailUnlockables = (props: InstanceDetailUnlockablesProps) => {
    const breakpoint = useBreakpoint();
    const {
        unlockableDownloadsHeadline,
        unlockableFilesHeadline,
        unlockableHiresCoverHeadline,
        instanceCoverHeadline
    } = useInstanceDetailPage();

    const { userId, walletId, instance, attachments } = useAppSelector((state: RootState) => ({
        userId: state.userSlice.user?.id,
        walletId: state.blockchainSlice.walletId,
        instance: state.productSlice.instance,
        attachments: state.productSlice.attachments
    }));

    const variant = useFullVariantByIdSsr();

    const covers = useFilterAttachments(VariantImageType.COVER_ARTWORK, attachments);

    const unlockables = useFilterAttachments(VariantImageType.ADDITIONAL_FILES, attachments);

    if (!!instance &&
        (!!userId && userId === instance.ownerId || walletId === instance.ownerWallet)) {
        return (
            <Accordion
                label={unlockableDownloadsHeadline?.headlineContent ?? ''}
                onlyMobil={true}
                initialOpen={false}
            >
                <div className={styles['unlockables-wrapper']}>
                    {breakpoint !== 'mobile' && (
                        <Typography
                            variant={TypographyVariantsEnum.HEADING3_BOLD}
                            element='h2'
                        >
                            {unlockableDownloadsHeadline.headlineContent}
                        </Typography>
                    )}
                    <div className={styles['content']}>
                        {unlockables && unlockables.length > 0 && (
                            <div>
                                <Typography
                                    variant={TypographyVariantsEnum.HEADING4_BOLD}
                                    element='h3'
                                >
                                    {unlockableFilesHeadline.headlineContent}
                                </Typography>
                                {unlockables?.map(({ url, name, id }) => {
                                    return (
                                        <InstanceDetailUnlockableFile
                                            key={id}
                                            name={name}
                                            link={url ?? ''}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {covers && covers.length > 0 ? (
                            <div>
                                <Typography
                                    variant={TypographyVariantsEnum.HEADING4_BOLD}
                                    element='h3'
                                >
                                    {unlockableHiresCoverHeadline?.headlineContent}
                                </Typography>
                                {covers?.map(({ urls, contentContentType }, index) => (
                                    <div
                                        style={index !== covers.length - 1 ? { marginBottom: '20px' } : undefined}
                                        key={urls?.original}
                                    >
                                        <InstanceDetailUnlockableImage
                                            notDownloadable={
                                                userId !== instance?.ownerId && walletId !== instance?.ownerWallet
                                            }
                                            variantId={variant.id}
                                            name={`variant-cover-art-${variant.id}`}
                                            contentType={contentContentType}
                                            imageUrl={urls?.original}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {props.instanceCoverArtUrl ? (
                            <div>
                                <Typography
                                    variant={TypographyVariantsEnum.HEADING4_BOLD}
                                    element='h3'
                                >
                                    {instanceCoverHeadline?.headlineContent}
                                </Typography>
                                <InstanceDetailUnlockableImage
                                    notDownloadable={
                                        userId !== instance?.ownerId && walletId !== instance?.ownerWallet
                                    }
                                    variantId={variant.id}
                                    name={`instance-cover-art-${instance.id}`}
                                    contentType={EnumToLanguageMapper.urlWithExtensionToMediaContentType(props.instanceCoverArtUrl)}
                                    key={props.instanceCoverArtUrl}
                                    imageUrl={props.instanceCoverArtUrl}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </Accordion>
        )
            ;
    } else {
        return <div/>;
    }
};
